import { type CollectionType, collection, keybinding } from './common';
import type { Command, NodeSpec, NodeType, Schema, Transaction } from './pm';
import { InputRule, inputRules, TextSelection } from './pm';
import {
  defaultGetParagraphNodeType,
  getNodeType,
  type PluginContext,
} from './pm-utils';

export type HorizontalRuleConfig = {
  name?: string;
  markdownShortcut?: boolean;
  getParagraphNodeType: (schema: Schema) => NodeType;
  keyInsert?: string | false;
};

type RequiredConfig = Required<HorizontalRuleConfig>;

const DEFAULT_CONFIG: RequiredConfig = {
  name: 'horizontalRule',
  markdownShortcut: true,
  getParagraphNodeType: defaultGetParagraphNodeType,
  keyInsert: 'Mod-Shift-h',
};

export function setupHorizontalRule(userConfig?: HorizontalRuleConfig) {
  const config = {
    ...DEFAULT_CONFIG,
    ...userConfig,
  };

  const { name } = config;

  const nodes = {
    [name]: {
      group: 'block',
      parseDOM: [{ tag: 'hr' }],
      toDOM: () => ['hr'],
    } satisfies NodeSpec,
  };

  const plugin = {
    inputRules: pluginInputRules(config),
    keybindings: pluginKeybindings(config),
  };

  return collection({
    id: 'horizontal_rule',
    nodes,
    plugin,
    command: {
      insertHorizontalRule: insertHorizontalRule(config),
    },
    markdown: markdown(config),
  });
}

/**
 * After an HR is in the doc, ensure a paragraph follows it and place the
 * caret at the start of that paragraph so typing continues below the rule.
 * Non-paragraph siblings (code blocks, tables, another HR) get a fresh
 * empty paragraph inserted before them.
 */
function ensureCaretAfterHorizontalRule(
  tr: Transaction,
  hrPos: number,
  paraType: NodeType,
): Transaction {
  const $hr = tr.doc.resolve(hrPos);
  const hrNode = $hr.nodeAfter;
  if (!hrNode) {
    return tr;
  }

  const afterHr = hrPos + hrNode.nodeSize;
  const $after = tr.doc.resolve(afterHr);
  const next = $after.nodeAfter;

  if (next?.type === paraType) {
    return tr.setSelection(TextSelection.create(tr.doc, afterHr + 1));
  }

  const para = paraType.createChecked();
  const withPara = tr.insert(afterHr, para);
  return withPara.setSelection(TextSelection.create(withPara.doc, afterHr + 1));
}

// PLUGINS
function pluginInputRules(config: RequiredConfig) {
  return ({ schema }: PluginContext) => {
    const { name, markdownShortcut } = config;
    if (!markdownShortcut) {
      return null;
    }

    const type = getNodeType(schema, name);
    const paraType = config.getParagraphNodeType(schema);

    return inputRules({
      rules: [
        new InputRule(
          /^(?:---|___\s|\*\*\*\s)$/,
          (state, match, start, end) => {
            if (!match[0]) {
              return null;
            }

            // Replace the whole textblock with an HR so the caret lands in a
            // real following paragraph (not a gap-cursor on the rule).
            const $start = state.doc.resolve(start);
            const blockStart = $start.before($start.depth);
            const blockEnd = $start.after($start.depth);
            const tr = state.tr.replaceWith(
              blockStart,
              blockEnd,
              type.createChecked(),
            );
            // After replaceWith, the HR sits at `blockStart`.
            return ensureCaretAfterHorizontalRule(
              tr,
              blockStart,
              paraType,
            ).scrollIntoView();
          },
        ),
      ],
    });
  };
}

function pluginKeybindings(config: RequiredConfig) {
  return keybinding(
    [[config.keyInsert, insertHorizontalRule(config)]],
    'horizontal-rule',
  );
}

// COMMANDS
function insertHorizontalRule(config: RequiredConfig): Command {
  const { name } = config;
  return (state, dispatch) => {
    const hrType = getNodeType(state.schema, name);
    const paraType = config.getParagraphNodeType(state.schema);
    const { $from } = state.selection;

    if ($from.parent.isTextblock) {
      const blockStart = $from.before($from.depth);
      const blockEnd = $from.after($from.depth);
      const hr = hrType.createChecked();
      let tr: Transaction;

      if ($from.parent.content.size === 0) {
        // Empty paragraph → replace with HR, then ensure a following para.
        tr = state.tr.replaceWith(blockStart, blockEnd, hr);
        tr = ensureCaretAfterHorizontalRule(tr, blockStart, paraType);
      } else {
        // Non-empty block → insert HR after it, then ensure caret below.
        tr = state.tr.insert(blockEnd, hr);
        tr = ensureCaretAfterHorizontalRule(tr, blockEnd, paraType);
      }

      if (dispatch) {
        dispatch(tr.scrollIntoView());
      }
      return true;
    }

    // Fallback: insert after the nearest block boundary.
    const insertPos = $from.after($from.depth);
    const hr = hrType.createChecked();
    let tr = state.tr.insert(insertPos, hr);
    tr = ensureCaretAfterHorizontalRule(tr, insertPos, paraType);
    if (dispatch) {
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

// MARKDOWN
function markdown(config: RequiredConfig): CollectionType['markdown'] {
  const { name } = config;
  return {
    nodes: {
      [name]: {
        toMarkdown: (state, node, parent, index) => {
          const markup = node.attrs.markup || '---';
          // A `---` on the first line of a document is a frontmatter fence,
          // so a doc-leading rule must use a different thematic-break marker
          // to survive a parse round trip.
          const isDocStart =
            parent.type === parent.type.schema.topNodeType && index === 0;
          state.write(isDocStart && markup === '---' ? '***' : markup);
          state.closeBlock(node);
        },
        parseMarkdown: {
          hr: {
            node: name,
          },
        },
      },
    },
  };
}
